import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMapGroupStyles } from "../hooks/useMapGroupStyles";
import { checkGroupSymbolSvg } from "../models/groupSymbolSvg";
import {
  CLUSTER_ICON_SCALE,
  CLUSTER_RADIUS,
  GROUP_COLOR_PRESETS,
  MapSettings,
  OPACITY_PERCENT,
  RING_BORDER_WIDTH,
  RING_PADDING,
  VEHICLE_ICON_SIZE,
} from "../models/mapSettings";
import { MAP_STYLE_KEYS, MapStyleKey } from "../models/mapStyles";
import { HAS_TRAFFIC_PROXY } from "../models/trafficNrw";
import FloatingPanel from "./FloatingPanel";
import { MAP_ADMIN_GROUP_NAME } from "@/constants/constants";
import { useGroups } from "@/hooks/groups/useGroups";

/** A heading over a run of related controls. */
function SettingGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack spacing={1}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      {children}
    </Stack>
  );
}

function SwitchSetting({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <FormControlLabel
      disabled={disabled}
      control={
        <Switch size="small" checked={checked} onChange={e => onChange(e.target.checked)} />
      }
      label={<Typography variant="body2">{label}</Typography>}
    />
  );
}

/**
 * A colour swatch.
 *
 * The browser's own colour input, as {@link StatusColorMenu} already uses for status colours —
 * the repo has no colour-picker component and this needs no dependency.
 */
function ColorSetting({
  label,
  value,
  fallback,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  /** Shown in the swatch while the setting is empty, i.e. while the theme decides. */
  fallback: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <TextField
      type="color"
      size="small"
      label={label}
      disabled={disabled}
      value={value || fallback}
      onChange={event => onChange(event.target.value)}
    />
  );
}

/**
 * A colour on its own, without a label around it.
 *
 * Wide and low, so the colour itself is the thing you see. The bordered `TextField` version is
 * right where the setting needs naming; in a list of groups the name is already on its own line
 * above, and the swatch only has to show a colour.
 */
function SwatchInput({
  value,
  fallback,
  disabled,
  ariaLabel,
  onChange,
}: {
  value: string;
  fallback: string;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (value: string) => void;
}) {
  return (
    <Box
      component="input"
      type="color"
      aria-label={ariaLabel}
      disabled={disabled}
      value={value || fallback}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      sx={{
        flex: 1,
        height: 28,
        minWidth: 0,
        p: 0,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "transparent",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    />
  );
}

function SliderSetting({
  label,
  value,
  min,
  max,
  step = 1,
  suffix,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="body2" color={disabled ? "text.disabled" : "text.primary"}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${value}${suffix ?? ""}`}
        </Typography>
      </Stack>
      <Slider
        size="small"
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(_event, next) => onChange(Array.isArray(next) ? next[0] : next)}
      />
    </Box>
  );
}

interface Props {
  settings: MapSettings;
  update: (patch: Partial<MapSettings>) => void;
  /** The theme's colours, shown wherever a setting is left to the theme. */
  themeColors: { icon: string; labelText: string; labelBackground: string };
  onClose: () => void;
}

/**
 * The map's settings, in a panel down the right-hand edge.
 *
 * Beside the map rather than in a dialog: every one of these changes something visible on the
 * map, and a reader dragging the ring width wants to watch it happen rather than confirm a dialog
 * and look afterwards.
 */
export function MapSettingsPanel({ settings, update, themeColors, onClose }: Props) {
  const { t } = useTranslation();
  const { data: groups = [] } = useGroups();
  const groupStyles = useMapGroupStyles();
  const canEdit = groupStyles.canEdit;
  const [symbolError, setSymbolError] = useState<string | null>(null);

  const configuredGroupIds = useMemo(
    () => groupStyles.groups.map(style => style.groupId),
    [groupStyles.groups],
  );

  // Every group in the database, not only the ones the header filter allows: the reader is
  // choosing which groups to colour, and a group they cannot currently see is still theirs to set
  // up. Sorted by name because a group tree in fetch order is unreadable.
  const groupOptions = useMemo(
    () =>
      [...groups]
        .filter(group => !!group.id)
        .map(group => ({ id: group.id as string, name: group.name ?? (group.id as string) }))
        .sort((first, second) => first.name.localeCompare(second.name)),
    [groups],
  );

  const nameById = useMemo(
    () => new Map(groupOptions.map(group => [group.id, group.name])),
    [groupOptions],
  );

  /**
   * Adding a group gives it a colour straight away, so two groups never start out alike.
   *
   * The whole list is worked out first and written once. Written one group at a time, each write
   * started from the same list the others did, so all but the last were lost -- and each new
   * group read the same length for its colour, which is what was meant to keep them apart.
   */
  const handleGroupSelection = (nextGroupIds: string[]) => {
    const kept = groupStyles.groups.filter(group => nextGroupIds.includes(group.groupId));
    const added = nextGroupIds.filter(groupId => !configuredGroupIds.includes(groupId));

    groupStyles.setGroups([
      ...kept,
      // Appended rather than prepended, so adding a group does not silently outrank the ones
      // already there.
      ...added.map((groupId, index) => ({
        groupId,
        color: GROUP_COLOR_PRESETS[(kept.length + index) % GROUP_COLOR_PRESETS.length],
      })),
    ]);
  };

  /** Reads the file, then either stores the symbol or says why it was refused. */
  const handleSymbolUpload = async (groupId: string, files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    setSymbolError(null);
    const checked = checkGroupSymbolSvg(await file.text());
    if (!checked.ok) {
      setSymbolError(checked.reason);
      return;
    }
    groupStyles.setGroupStyle(groupId, { svg: checked.svg });
  };

  return (
    // Pinned beside the control stack rather than below it: below, the panel covered the very
    // button that opens and closes it.
    <FloatingPanel
      title={t("map.settings.title", "Karteneinstellungen")}
      placement="topRight"
      minWidth={300}
      maxWidth={300}
      maxHeight="calc(100% - 72px)"
      disableContentPadding
      onClose={onClose}
    >

      <Stack spacing={2} divider={<Divider flexItem />} sx={{ p: 1.5 }}>
        <SettingGroup title={t("map.settings.showTitle", "Anzeigen")}>
          <SwitchSetting
            label={t("map.settings.showZones", "Zonen anzeigen")}
            checked={settings.showZones}
            onChange={showZones => update({ showZones })}
          />
          <SwitchSetting
            label={t("map.settings.showTraffic", "Verkehrslage anzeigen")}
            checked={settings.showTraffic}
            disabled={!HAS_TRAFFIC_PROXY}
            onChange={showTraffic => update({ showTraffic })}
          />
          {HAS_TRAFFIC_PROXY ? null : (
            <Typography variant="caption" color="text.secondary">
              {t(
                "map.settings.trafficUnavailable",
                "Die Verkehrslage ist nur in der Server-Version verfügbar.",
              )}
            </Typography>
          )}
        </SettingGroup>

        <SettingGroup title={t("map.settings.basemap", "Kartenhintergrund")}>
          <FormControl size="small" fullWidth>
            <InputLabel id="map-style">{t("map.settings.basemap", "Kartenhintergrund")}</InputLabel>
            <Select
              labelId="map-style"
              label={t("map.settings.basemap", "Kartenhintergrund")}
              value={settings.mapStyle}
              onChange={event => update({ mapStyle: event.target.value as MapStyleKey })}
            >
              {MAP_STYLE_KEYS.map(key => (
                <MenuItem key={key} value={key}>
                  {t(`map.settings.basemapName.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </SettingGroup>

        <SettingGroup title={t("map.settings.namesTitle", "Fahrzeugnamen")}>
          <SwitchSetting
            label={t("map.showAssetNames", "Fahrzeugnamen anzeigen")}
            checked={settings.showAssetNames}
            onChange={showAssetNames => update({ showAssetNames })}
          />
          <SwitchSetting
            label={t("map.settings.showPowertrainBadges", "Antriebs-Kürzel anzeigen")}
            checked={settings.showPowertrainBadges}
            disabled={!settings.showAssetNames}
            onChange={showPowertrainBadges => update({ showPowertrainBadges })}
          />
          <ColorSetting
            label={t("map.settings.nameColor", "Schriftfarbe")}
            value={settings.assetNameColor}
            fallback={themeColors.labelText}
            disabled={!settings.showAssetNames}
            onChange={assetNameColor => update({ assetNameColor })}
          />
          <ColorSetting
            label={t("map.settings.nameBackground", "Hintergrund")}
            value={settings.assetNameBackgroundColor}
            fallback={themeColors.labelBackground}
            disabled={!settings.showAssetNames}
            onChange={assetNameBackgroundColor => update({ assetNameBackgroundColor })}
          />
        </SettingGroup>

        <SettingGroup title={t("map.settings.iconsTitle", "Fahrzeugsymbole")}>
          <SliderSetting
            label={t("map.settings.iconSize", "Größe")}
            value={settings.vehicleIconSizePx}
            min={VEHICLE_ICON_SIZE.min}
            max={VEHICLE_ICON_SIZE.max}
            suffix=" px"
            onChange={vehicleIconSizePx => update({ vehicleIconSizePx })}
          />
          <ColorSetting
            label={t("map.settings.iconColor", "Farbe")}
            value={settings.vehicleIconColor}
            fallback={themeColors.icon}
            onChange={vehicleIconColor => update({ vehicleIconColor })}
          />
        </SettingGroup>

        <SettingGroup title={t("map.settings.ringTitle", "Kreis um das Fahrzeug")}>
          <ColorSetting
            label={t("map.settings.ringFillColor", "Füllfarbe")}
            value={settings.ringFillColor}
            fallback="#ffffff"
            onChange={ringFillColor => update({ ringFillColor })}
          />
          <SliderSetting
            label={t("map.settings.ringFillOpacity", "Füllung")}
            value={settings.ringFillOpacity}
            min={OPACITY_PERCENT.min}
            max={OPACITY_PERCENT.max}
            suffix=" %"
            onChange={ringFillOpacity => update({ ringFillOpacity })}
          />
          <ColorSetting
            label={t("map.settings.ringBorderColor", "Randfarbe")}
            value={settings.ringBorderColor}
            fallback={themeColors.icon}
            onChange={ringBorderColor => update({ ringBorderColor })}
          />
          <SliderSetting
            label={t("map.settings.ringBorderOpacity", "Rand")}
            value={settings.ringBorderOpacity}
            min={OPACITY_PERCENT.min}
            max={OPACITY_PERCENT.max}
            suffix=" %"
            onChange={ringBorderOpacity => update({ ringBorderOpacity })}
          />
          <SliderSetting
            label={t("map.settings.ringBorderWidth", "Randstärke")}
            value={settings.ringBorderWidthPx}
            min={RING_BORDER_WIDTH.min}
            max={RING_BORDER_WIDTH.max}
            suffix=" px"
            onChange={ringBorderWidthPx => update({ ringBorderWidthPx })}
          />
          <SliderSetting
            label={t("map.settings.ringPadding", "Abstand zum Symbol")}
            value={settings.ringPaddingPx}
            min={RING_PADDING.min}
            max={RING_PADDING.max}
            suffix=" px"
            onChange={ringPaddingPx => update({ ringPaddingPx })}
          />
        </SettingGroup>

        <SettingGroup title={t("map.settings.clusterTitle", "Zusammenfassen")}>
          <SwitchSetting
            label={t("map.settings.clusteringEnabled", "Fahrzeuge zusammenfassen")}
            checked={settings.clusteringEnabled}
            onChange={clusteringEnabled => update({ clusteringEnabled })}
          />
          <SliderSetting
            label={t("map.settings.clusterRadius", "Reichweite")}
            value={settings.clusterRadiusPx}
            min={CLUSTER_RADIUS.min}
            max={CLUSTER_RADIUS.max}
            suffix=" px"
            disabled={!settings.clusteringEnabled}
            onChange={clusterRadiusPx => update({ clusterRadiusPx })}
          />
          <SliderSetting
            label={t("map.settings.clusterSize", "Größe der Blasen")}
            value={settings.clusterIconScalePercent}
            min={CLUSTER_ICON_SCALE.min}
            max={CLUSTER_ICON_SCALE.max}
            step={5}
            suffix=" %"
            disabled={!settings.clusteringEnabled}
            onChange={clusterIconScalePercent => update({ clusterIconScalePercent })}
          />
        </SettingGroup>


        <SettingGroup title={t("map.settings.groupsTitle", "Gruppenfarben")}>
          <Typography variant="caption" color="text.secondary">
            {t("map.settings.groupsShared", "Farben und Symbole gelten für alle Benutzer.")}
          </Typography>

          {canEdit ? null : (
            <Typography variant="caption" color="text.secondary">
              {t("map.settings.groupsNoPermission", { group: MAP_ADMIN_GROUP_NAME })}
            </Typography>
          )}

          <FormControl size="small" fullWidth disabled={!canEdit}>
            <InputLabel id="map-groups">{t("map.settings.groups", "Gruppen")}</InputLabel>
            <Select
              labelId="map-groups"
              multiple
              input={<OutlinedInput label={t("map.settings.groups", "Gruppen")} />}
              value={configuredGroupIds}
              renderValue={selected =>
                selected.map(groupId => nameById.get(groupId) ?? groupId).join(", ")
              }
              onChange={event =>
                handleGroupSelection(
                  typeof event.target.value === "string"
                    ? event.target.value.split(",")
                    : event.target.value,
                )
              }
            >
              {groupOptions.map(group => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {groupStyles.groups.map(style => (
            <Stack
              key={style.groupId}
              spacing={0.5}
              sx={{ p: 1, borderRadius: 1, bgcolor: "action.hover" }}
            >
              {/*
                The name on a line of its own. Squeezed into the swatch's own label it was
                truncated to a letter and a half, which told the reader nothing about which group
                they were colouring.
              */}
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {nameById.get(style.groupId) ?? style.groupId}
              </Typography>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <SwatchInput
                  value={style.color}
                  fallback={themeColors.icon}
                  disabled={!canEdit}
                  ariaLabel={t("map.settings.groupColor", "Farbe der Gruppe")}
                  onChange={color => groupStyles.setGroupStyle(style.groupId, { color })}
                />

                <Tooltip title={t("map.settings.uploadSymbol", "Symbol hochladen")}>
                  <IconButton
                    size="small"
                    component="label"
                    disabled={!canEdit}
                    aria-label={t("map.settings.uploadSymbol", "Symbol hochladen")}
                  >
                    <UploadFileIcon fontSize="small" color={style.svg ? "primary" : undefined} />
                    <input
                      type="file"
                      accept=".svg,image/svg+xml"
                      hidden
                      onChange={event => handleSymbolUpload(style.groupId, event.target.files)}
                    />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t("map.settings.removeGroup", "Gruppe entfernen")}>
                  <IconButton
                    size="small"
                    disabled={!canEdit}
                    onClick={() => groupStyles.removeGroup(style.groupId)}
                    aria-label={t("map.settings.removeGroup", "Gruppe entfernen")}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              {style.svg ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    {t("map.settings.symbolSet", "Eigenes Symbol")}
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={!canEdit}
                    onClick={() => groupStyles.setGroupStyle(style.groupId, { svg: undefined })}
                    aria-label={t("map.settings.removeSymbol", "Symbol entfernen")}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ) : null}
            </Stack>
          ))}

          {symbolError ? (
            <Typography variant="caption" color="error">
              {t("map.settings.symbolError." + symbolError)}
            </Typography>
          ) : null}

          {groupStyles.groups.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              {t("map.settings.groupsHint", "Ohne Auswahl haben alle Fahrzeuge dieselbe Farbe.")}
            </Typography>
          ) : null}
        </SettingGroup>
      </Stack>
    </FloatingPanel>
  );
}
